import React from 'react';
import { Box } from '@mui/material';
import StatusBadge from './StatusBadge';

// Icon prefix for a single remark fragment.
function remarkIcon(part) {
  if (part.includes('Late In')) return '🔴';
  if (part.includes('Early In')) return '🟢';
  if (part.includes('Early Out') || part.includes('Late Out')) return '🟡';
  if (part === 'On Time') return '✅';
  if (part.includes('Missed')) return '⛔';
  return null;
}

// Variant picker for a single remark fragment.
function remarkVariant(part) {
  if (part.includes('Missed')) return 'dangerSoft';
  if (
    part.includes('Late In') || part.includes('Early In') ||
    part.includes('Early Out') || part.includes('Late Out')
  ) return 'warning';
  if (part === 'On Time') return 'success';
  return 'warningSoft';
}

/**
 * VisitStatusCell — renders the full status column for a visit row/card.
 *
 * Handles:
 *  - Optional overlap warning
 *  - Multi-part remarks (e.g. "Late In by 5 mins, Early Out by 3 mins")
 *  - Missed / On Time / Late-Early color coding via StatusBadge variants
 *
 * Props:
 *  - remark: string from calculateTimeDifference().remark
 *  - hasOverlap: bool
 *  - overlapMinutes: total overlap minutes across this visit's conflicts
 *  - size: badge size preset (default 'sm')
 *  - suppressOnTimeWhenOverlap: when true and hasOverlap, hides the "On Time" badge
 */
export default function VisitStatusCell({
  remark,
  hasOverlap = false,
  overlapMinutes = 0,
  size = 'sm',
  suppressOnTimeWhenOverlap = true,
}) {
  const parts = (remark || '').split(', ').filter(Boolean);
  const showRemark = !(hasOverlap && suppressOnTimeWhenOverlap && remark === 'On Time');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
      {hasOverlap && (
        <StatusBadge
          variant="dangerSoft"
          size={size}
          icon="⚠️"
          label="OVERLAP"
          detail={`${overlapMinutes} min`}
        />
      )}
      {showRemark && parts.map((part, idx) => (
        <StatusBadge
          key={`${part}-${idx}`}
          variant={remarkVariant(part)}
          size={size}
          icon={remarkIcon(part)}
          label={part}
        />
      ))}
    </Box>
  );
}
